
/**
 * Module dependencies.
 */

import * as linkify from 'linkifyjs';
import { Product as BaseProduct } from 'types/product';
import { Button, CopyToClipboard, Heading, Text, typography } from '@slyk/design-system';
import { Container } from 'client/components/core/container';
import { Icon } from 'react-components/media';
import { Media, VideoSource } from 'types/media';
import { MediaGallery, MediaGalleryProps } from './media-gallery';
import { color, media } from 'react-components/styles';
import { getImageSources } from 'client/core/utils/image';
import { unitsOfAccount } from 'client/core/constants/product';
import { useTranslate } from '@slyk/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import linkIcon32px from 'client/assets/svg/32/link.svg';
import referralIcon20px from 'client/assets/svg/20/referral.svg';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

/**
 * `Product` type.
 */

type Product = BaseProduct & {
  bonusDefaultCurrency: string | null;
  displayPrice: string;
  displayPriceConverted: string;
  gallery: Array<Media>;
  video: VideoSource | null;
};

/**
 * `Props` type.
 */

type Props = {
  getSlykApp: () => void;
  product: Product;
};

/**
 * Unit of account types.
 */

const unitOfAccountTypes = Object.keys(unitsOfAccount);

/*
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  ${media.min('xl')`
    padding-left: 0;
    padding-right: 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, max-content);
  padding-top: 16px;
  row-gap: 16px;

  ${media.min('sm')`
    gap: 24px;
    grid-template-columns: repeat(8, 1fr);
    grid-template-rows: max-content 1fr;
    padding: 64px 0 56px;
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(12, 1fr);
    padding-bottom: 48px;
  `}

  ${media.min('xl')`
    column-gap: 32px;
    padding: 104px 0 72px;
  `}
`;

/**
 * `CarrouselWrapper` styled component.
 */

const CarrouselWrapper = styled.div`
  grid-column: 1 / -1;
  grid-row: 1;

  ${media.min('sm')`
    grid-column: 1 / span 3;
    grid-row: 1 / -1;
  `}

  ${media.min('lg')`
    grid-column: 1 / span 5;
  `}

  ${media.min('xl')`
    grid-column: 2 / span 4;
  `}
`;

/**
 * `Board` styled component.
 */

const Board = styled.div`
  background-color: ${color('gray200')};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  grid-row: 2;
  padding: 16px;

  ${media.min('sm')`
    grid-column: 4 / -1;
    grid-row: 1;
    padding: 24px;
  `}

  ${media.min('lg')`
    grid-column: 6 / -1;
  `}

  ${media.min('xl')`
    grid-column: 6 / -2;
    margin-bottom: 24px;
    margin-left: 32px;
  `}
`;

/**
 * `Price` styled component.
 */

const Price = styled(Text).attrs({ size: 'large' })`
  color: ${color('nightBlue500')};
  margin-top: 16px;
  word-break: break-word;
`;

/**
 * `AdditionalInfo` styled component.
 */

const AdditionalInfo = styled.span`
  margin-left: 3px;
`;

/**
 * `PriceConverted` styled component.
 */

const PriceConverted = styled(Text).attrs({ size: 'normal' })`
  color: ${color('gray700')};
`;

/**
 * `TagsWrapper` styled component.
 */

const TagsWrapper = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;

  ${media.min('sm')`
    margin-top: 16px;
  `}
`;

/**
 * `Tag` styled component.
 */

const Tag = styled.div`
  ${typography.styles.captionSmall}

  background-color: transparent;
  border: 1px solid ${color('blue200')};
  border-radius: 8px;
  color: ${color('primary')};
  display: flex;
  gap: 6px;
  height: max-content;
  padding: 3px 5px;
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  column-gap: 8px;
  display: grid;
  grid-template-columns: 1fr max-content;
  margin-top: 24px;
`;

/**
 * `Description` styled component.
 */

const Description = styled(ReactMarkdown)`
  ${typography.styles.textNormal}

  color: ${color('secondary')};
  grid-column: 1 / -1;
  grid-row: 3;
  margin-bottom: 24px;
  position: relative;
  word-break: break-word;

  ${media.min('sm')`
    grid-column: 4 / -1;
    grid-row: 2;
  `}

  ${media.min('lg')`
    grid-column: 6 / -1;
  `}

  ${media.min('xl')`
    grid-column: 6 / -2;
    margin-bottom: 40px;
    margin-left: 32px;
  `}

  p {
    margin: 0;
  }

  ul,
  ol {
    list-style: revert;
    margin: revert;
    padding: revert;
    text-align: start;
  }
`;

/**
 * Export `DetailsLayout` component.
 */

export function DetailsLayout({ getSlykApp, product }: Props): JSX.Element {
  const {
    bonus,
    bonusDefaultCurrency,
    description,
    displayPrice,
    displayPriceConverted,
    gallery,
    name,
    typeCode,
    unitOfAccount,
    video
  } = product ?? {};

  const { translate } = useTranslate();
  const url = window.location.href;
  const noImageSources = getImageSources('no-image/product', 'webp');
  const mediaGalleryNormalized = useMemo(() => {
    const videoGallery = video ? [{
      original: video.url ?? null,
      thumbnail: video.thumbnailUrl ?? null,
      type: video.type ?? null
    }] : [];

    const mediaGallery = gallery?.map(({ image }: any) => ({
      original: image.url ?? null,
      thumbnail: image.url ?? null,
      type: image.type ?? null
    })) ?? [];

    return videoGallery.concat(mediaGallery);
  }, [gallery, video]);

  const [mediaGallery, setMediaGallery] = useState<Array<MediaGalleryProps>>(mediaGalleryNormalized);
  const handleThumbnailError = useCallback(() => {
    const newMediaGallery = mediaGalleryNormalized.map(item => {
      return {
        ...item,
        thumbnail: noImageSources.defaultUrl
      };
    });

    setMediaGallery(newMediaGallery);
  }, [mediaGalleryNormalized, noImageSources.defaultUrl]);

  const formattedDescription = useMemo(() => {
    if (!description) {
      return description;
    }

    const linkableList = linkify.find(description) ?? [];
    const urlLinks = linkableList.filter(({ type }) => type === 'url');

    return urlLinks.reduce((result, { value }) => {
      const formattedValue = value.replace('\\_', '_');

      return result.replace(value, formattedValue);
    }, description);
  }, [description]);

  return (
    <StyledContainer>
      <Grid>
        <CarrouselWrapper>
          <MediaGallery
            mediaGallery={mediaGallery}
            onThumbnailError={handleThumbnailError}
          />
        </CarrouselWrapper>

        <Board>
          <Heading
            as={'h2'}
            fontWeight={600}
            level={'h3'}
          >
            {name}
          </Heading>

          {!!displayPrice && (
            <Price>
              {displayPrice}

              {!!unitOfAccount && (
                <AdditionalInfo>
                  {unitOfAccountTypes.includes(unitOfAccount) ? translate('products.per', {
                    unitOfAccount: translate(`products.unitOfAccount.${unitOfAccount}`)
                  }) : translate('products.per', { unitOfAccount })}
                </AdditionalInfo>
              )}
            </Price>
          )}

          {!!displayPriceConverted && (
            <PriceConverted>
              {displayPriceConverted}
            </PriceConverted>
          )}

          <TagsWrapper>
            <Tag>
              {translate([`products.type.${typeCode}`, 'products.type.fallback'])}
            </Tag>

            {!!bonus && (
              <Tag>
                <Icon
                  aria-hidden
                  color={color('blue500')}
                  icon={referralIcon20px}
                  size={'20px'}
                />

                {`${translate('products.details.earn')} ${bonus}`}

                {!!bonusDefaultCurrency && `/ ${bonusDefaultCurrency}`}
              </Tag>
            )}
          </TagsWrapper>

          <Actions>
            <Button
              colorTheme={'success'}
              onClick={getSlykApp}
              size={'large'}
            >
              {translate('common.actions.buyInApp')}
            </Button>

            <CopyToClipboard
              aria-label={translate('copyToClipboard.ariaLabel')}
              colorTheme={'gray'}
              copiedLabel={translate('copyToClipboard.copied')}
              icon={linkIcon32px}
              iconSize={'large'}
              size={'large'}
              value={url}
            />
          </Actions>
        </Board>

        {!!formattedDescription && (
          <Description remarkPlugins={[remarkGfm]}>
            {formattedDescription}
          </Description>
        )}
      </Grid>
    </StyledContainer>
  );
}
